import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Button, Col, Flex, Form, Input, Row, Radio, Select, InputNumber, Tooltip } from 'antd';
import MainCard from 'components/MainCard';
import { toast } from 'react-toastify';
import { toastConfig } from 'config';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetLynPropertyQuery, useGetSingleIdentityMutation } from 'store/features/process/processApi';

import IconButton from 'components/@extended/IconButton';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import AddIconButton from 'components/button/AddIconButton';
import DeleteIconButton from 'components/button/DeleteIconButton';

import { MinusCircleOutlined, PlusCircleOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import DetailPageHeader from 'components/header/DetailPageHeader';
import CustomInputLabel from 'components/Label/CustomInputLabel';
import CustomTextInput from 'components/inputs/CustomTextInput';
import CustomDateInput from 'components/inputs/CustomDateInput';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import SearchMemberModal from './SearchMemberModal';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import { formatDateToUTC } from 'utils/utils';
import SectionCard from 'components/section/SectionCard';
import FormSubmitButton from 'components/button/FormSubmitButton';

function COECommittee() {
	const theme = useTheme();
	const navigate = useNavigate();
	const { state } = useLocation();
	const [form] = Form.useForm();

	const [CreateSingleIdentity, { isLoading: isCreateLoading, isSuccess: isCreateSuccess, isError: isCreateError }] = useGetSingleIdentityMutation();

	const handleSubmit = () => {
		if (state?.processCode) {
			CreateSingleIdentity({
				operation: 'add-single',
				processCode: state?.processCode,
				suffixCode: '',
				data: submitData,
			});
		}
	};

	const { committeeDetailsState } = state?.state || {};
	const { data: roleListRes } = useGetLynPropertyQuery(
		{
			type: 'FORM',
			fieldCode: '10042',
			propertyCode: 'PROPERTY_role',
			processVersion: '24A',
			status: 'Active',
		},
		{
			// refetchOnMountOrArgChange: true,
			keepUnusedDataFor: 0,
		}
	);

	const [getUserIdentity, { data: userIdentityList }] = useSendControlDataMutation();

	const [showModal, setShowModal] = useState(false);
	const [selectedModalRow, setSelectedModalRow] = useState({
		name: null,
		index: null,
	});

	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [modalInfo, setModalInfo] = useState({
		head: 'Committee',
		action: committeeDetailsState ? 'Update' : 'Submit',
	});
	const [submitData, setSubmitData] = useState(null);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event, fields, fieldsOpt) => {
		const { active, over } = event;

		if (active?.id && over?.id && active?.id !== over?.id) {
			const oldIndex = fields.findIndex((field) => `${field.key}` === active.id);
			const newIndex = fields.findIndex((field) => `${field.key}` === over.id);

			// Update the state or form fields with reordered data
			fieldsOpt.move(oldIndex, newIndex);
		}
	};

	useEffect(() => {
		if (!isCreateLoading && isCreateSuccess) {
			navigate(-1, { replace: true });
			toast.success('Success! Action completed ', toastConfig);
		}
		if (!isCreateLoading && isCreateError) {
			toast.error('Failed! Action not completed', toastConfig);
		}
	}, [isCreateSuccess, isCreateError]);

	const handleModalSubmit = (data) => {
		setSubmitData({
			...data,
			fromDate: formatDateToUTC(data?.fromDate),
		});
		setShowConfirmationModal(true);
		if (committeeDetailsState) {
			setModalInfo({
				head: 'Committee',
				action: 'update',
			});
		} else {
			setModalInfo({
				head: 'Committee',
				action: 'submit',
			});
		}
	};

	useEffect(() => {
		const formattedPayload = {
			data: '',
			processCode: state?.processCode,
			scriptName: 'employee-data-v2',
		};

		getUserIdentity(formattedPayload);
	}, [state?.processCode]);

	const dataList = useMemo(() => {
		const userOptions = userIdentityList?.responseData?.users?.map((u) => ({
			label: u.userFullName,
			value: u.userId,
			userFullName: u.userFullName,
		}));

		const positionOptions = userIdentityList?.responseData?.positions?.map((p) => ({
			label: p.positionName,
			value: p.propertyValue,
			userFullName: p.userFullName,
		}));

		const processRoleOptions = userIdentityList?.responseData?.processRoles?.map((r) => ({
			label: r.processRoleName,
			value: r.propertyValue,
			userFullName: r.userFullName,
		}));

		return {
			users: userOptions,
			positions: positionOptions,
			processRoles: processRoleOptions,
		};
	}, [userIdentityList?.status]);

	const roleList = useMemo(() => {
		return roleListRes?.lynProperties?.map((item) => ({
			label: item?.displayName,
			value: item?.propertyValue,
		}));
	}, [roleListRes?.lynProperties]);

	return (
		<>
			<Box>
				<DetailPageHeader
					image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
					title={committeeDetailsState ? `Edit ${state?.processName}` : `Create ${state?.processName}`}
					showAuto
					autoFirst
					pinStorageKey={`committee:${committeeDetailsState ? 'edit' : 'create'}:auto:${state?.processCode ?? 'default'}`}
				/>

				<SectionCard>
					<Form.Provider
						onFormFinish={(name, { values, forms }) => {
							const { mainForm } = forms;

							if (name === 'committeeMembers') {
								const prev = mainForm.getFieldValue('committeeMembers') || [];
								const updated = [...prev];
								updated[selectedModalRow.index] = {
									...prev[selectedModalRow.index], // keep old
									...values, // merge new
								};
								mainForm.setFieldValue('committeeMembers', updated);
							} else if (name === 'committeeSecretary') {
								const prev = mainForm.getFieldValue('committeeSecretary') || [];
								const updated = [...prev];
								updated[selectedModalRow.index] = {
									...prev[selectedModalRow.index],
									...values,
								};
								mainForm.setFieldValue('committeeSecretary', updated);
							}
						}}
					>
						<Form
							form={form}
							layout="vertical"
							id="coe-committee"
							initialValues={{
								committeeMembers: [null],
								committeeSecretary: [null],
							}}
							autoComplete="off"
							requiredMark={false}
							name="mainForm"
							onFinish={handleModalSubmit}
						>
							<AntdValuePreview form={form} show={true} />
							<Row gutter={[16, 0]}>
								{/* <Col xs={24} md={8} lg={6}>
									<CustomInputLabel label="Effective from" head={true} />
								</Col> */}
								<Col xs={24} sm={24} md={12} xl={12}>
									<CustomTextInput
										form={form}
										label="Committee Name"
										name="title"
										placeholder="Write here"
										rules={[
											{
												required: true,
												message: 'Committee name is required.',
											},
										]}
									/>
								</Col>
								<Col xs={24} sm={24} md={12} xl={12}>
									<CustomDateInput
										form={form}
										label="Effective From"
										name="fromDate"
										placeholder="Select date"
										rules={[
											{
												required: true,
												message: 'Effective from date is required.',
											},
										]}
									/>
								</Col>

								{/* <Col xs={24} md={8} lg={6}>
									<CustomInputLabel label="Committee name" head={true} />
								</Col> */}

								<Col xs={24} sm={24} md={24} xl={24}>
									<Form.Item
										size="medium"
										name="purpose"
										label="Scope of Work / Terms of Reference"
										rules={[
											{
												required: true,
												message: 'Details is required.',
											},
										]}
									>
										<Input.TextArea rows={2} placeholder="Write here" />
									</Form.Item>

									{/* <CustomTextInput
										form={form}
										label="Details"
										name="purpose"
										placeholder="Write here"
										rules={[
											{
												required: true,
												message: 'Details is required.',
											},
										]}
									/> */}
								</Col>
								{/* <Col xs={24} sm={24} md={24} xl={24}>
									<CustomTextInput
										form={form}
										label="Details"
										name="purpose"
										placeholder="Write here"
										rules={[
											{
												required: true,
												message: 'Details is required.',
											},
										]}
									/>
								</Col> */}

								<Col xs={24} sm={24} md={24} xl={24}>
									<Typography
										variant="h4"
										sx={{
											fontWeight: 600,
											fontSize: '18px',
											color: '#1a3b5d',
											marginBottom: '16px',
											padding: '0 0 12px 0',
											borderBottom: '1px solid #e0e6ed',
										}}
									>
										Committee Composition
									</Typography>
								</Col>
								{/* <MainCard content={false} sx={{ overflowX: 'auto', width: '100%', mb: 2 }}> */}
								<Form.List name="committeeMembers">
									{(fields, fieldsOpt) => (
										<DndContext
											sensors={sensors}
											collisionDetection={closestCenter}
											onDragEnd={(e) => handleDragEnd(e, fields, fieldsOpt)}
											modifiers={[restrictToVerticalAxis, restrictToParentElement]}
										>
											<TableContainer
												sx={{
													'& td, & th': {
														border: '1px solid rgb(224, 224, 224)',
														p: 1,
													},
													'& .ant-form-item': {
														marginBottom: 0,
													},
													mb: 2,
													px: 1,
												}}
											>
												<Table aria-labelledby="committeeCompositionTable">
													<TableHead sx={{ backgroundColor: 'rgb(246, 245, 245)' }}>
														<TableRow>
															<TableCell sx={{ minWidth: '60px', width: '5%' }}></TableCell>
															<TableCell sx={{ minWidth: '180px', width: '18%' }}>Committee</TableCell>
															<TableCell sx={{ minWidth: '120px', width: '12%' }}>Nominated As</TableCell>
															<TableCell sx={{ minWidth: '180px', width: '12%' }}>Role</TableCell>
															<TableCell sx={{ minWidth: '100px', width: '8%' }}>Approval Sequence</TableCell>
															<TableCell sx={{ minWidth: '100px', width: '10%' }}>Voting Right</TableCell>
															<TableCell sx={{ minWidth: '100px', width: '10%' }}>Is Voting Must?</TableCell>
															<TableCell sx={{ minWidth: '100px', width: '10%' }}>Voting Score</TableCell>
															<TableCell align="center" sx={{ minWidth: '100px', width: '10%' }}>
																Action
															</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{fields?.map((field, index) => (
															<SortableContext key={field.key} items={fields.map((field) => `${field.key}`)}>
																<Form.Item name={[field.name, 'positionType']} noStyle />
																<Form.Item name={[field.name, 'positionValue']} noStyle />
																<DraggableRow id={`${field.key}`}>
																	<TableCell sx={{ width: '15rem', p: 0 }}>
																		<Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
																			<Form.Item
																				name={[field.name, 'personName']}
																				style={{ margin: 0, flex: 1 }} // remove default margin + expand input
																			>
																				<Input placeholder="Person name" disabled />
																			</Form.Item>
																			<Button
																				type="dashed"
																				shape="circle"
																				icon={<UsergroupAddOutlined />}
																				onClick={() => {
																					setShowModal(true);
																					setSelectedModalRow({
																						name: 'committeeMembers',
																						index: field.name,
																					});
																				}}
																			/>
																		</Box>
																	</TableCell>

																	<TableCell sx={{ width: '10rem' }}>
																		<Form.Item
																			name={[field.name, 'nominatedAs']}
																			rules={[
																				{
																					required: true,
																					message: 'Committee role is required.',
																				},
																			]}
																		>
																			<Select
																				allowClear
																				showSearch
																				// style={{ width: '100%' }}
																				placeholder="Select from the list"
																				options={[
																					{
																						label: 'Position',
																						value: 'positions',
																					},
																					{
																						label: 'Employee',
																						value: 'users',
																					},
																					{
																						label: 'Process Role',
																						value: 'processRoles',
																					},
																				]}
																				disabled
																			/>
																		</Form.Item>
																	</TableCell>

																	<TableCell>
																		<Form.Item
																			name={[field.name, 'positionName']}
																			rules={[
																				{
																					required: true,
																					message: 'Committee role is required.',
																				},
																			]}
																		>
																			<Select
																				allowClear
																				showSearch
																				// style={{ width: '100%' }}
																				placeholder="Select from the list"
																				options={roleList}
																			/>
																		</Form.Item>
																	</TableCell>

																	<TableCell>
																		<Form.Item
																			name={[field.name, 'serial']}
																			rules={[
																				{
																					required: true,
																					message: 'Committee role is required.',
																				},
																			]}
																		>
																			<InputNumber
																				min={1}
																				style={{ width: '100%' }}
																				placeholder="Write here"
																			/>
																		</Form.Item>
																	</TableCell>

																	<TableCell>
																		<Form.Item name={[field.name, 'canVote']}>
																			<Select
																				allowClear
																				showSearch
																				// style={{ width: '100%' }}
																				placeholder="Select from the list"
																				options={[
																					{ label: 'Yes', value: 'yes' },
																					{ label: 'No', value: 'no' },
																				]}
																			/>
																		</Form.Item>
																	</TableCell>

																	<TableCell>
																		<Form.Item name={[field.name, 'isMandatory']}>
																			<Select
																				allowClear
																				showSearch
																				style={{ width: '100%' }}
																				placeholder="Select from the list"
																				options={[
																					{ label: 'Yes', value: 'yes' },
																					{ label: 'No', value: 'no' },
																				]}
																			/>
																		</Form.Item>
																	</TableCell>

																	<TableCell>
																		<Form.Item
																			name={[field.name, 'points']}
																			style={{ margin: 0, flex: 1 }} // remove default margin + expand input
																		>
																			<Input placeholder="Write here" />
																		</Form.Item>
																	</TableCell>

																	<TableCell align="center">
																		{fields.length === 1 && index === 0 && (
																			<Tooltip title="Add Row">
																				{/* <PlusCircleOutlined
																						style={{
																							fontSize: '20px',
																							color: '#1890ff',
																							cursor: 'pointer',
																						}}
																						
																					/> */}
																				<AddIconButton onClick={() => fieldsOpt.add()} />
																			</Tooltip>
																		)}

																		{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																			<Tooltip title="Delete Row">
																				{/* <MinusCircleOutlined
																						style={{
																							fontSize: '20px',
																							color: '#ff4d4f',
																							cursor: 'pointer',
																						}}
																						onClick={() => fieldsOpt.remove(field.name)}
																					/> */}
																				<DeleteIconButton onClick={() => fieldsOpt.remove(field.name)} />
																			</Tooltip>
																		)}

																		{fields.length > 1 && index === fields.length - 1 && (
																			<>
																				<Tooltip title="Add Row">
																					{/* <PlusCircleOutlined
																							style={{
																								fontSize: '20px',
																								color: '#1890ff',
																								marginRight: '12px',
																								cursor: 'pointer',
																							}}
																							onClick={() => fieldsOpt.add()}
																						/> */}
																					<AddIconButton onClick={() => fieldsOpt.add()} />
																				</Tooltip>
																				<Tooltip title="Delete Row">
																					{/* <MinusCircleOutlined
																							style={{
																								fontSize: '20px',
																								color: '#ff4d4f',
																								cursor: 'pointer',
																							}}
																							onClick={() => fieldsOpt.remove(field.name)}
																						/> */}
																					<DeleteIconButton onClick={() => fieldsOpt.remove(field.name)} />
																				</Tooltip>
																			</>
																		)}

																		{fields.length > 1 && index === 0 && <span></span>}
																	</TableCell>
																</DraggableRow>
															</SortableContext>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										</DndContext>
									)}
								</Form.List>

								<Col xs={24} sm={24} md={12} xl={12}>
									<Form.Item
										label="Quorum"
										name="quorum"
										rules={[
											{
												required: true,
												message: 'Quorum is required',
											},
										]}
									>
										<InputNumber min={0} style={{ width: '100%' }} placeholder="Write here" />
									</Form.Item>
								</Col>

								<Col xs={24} sm={24} md={24} xl={24}>
									<Typography
										variant="h4"
										sx={{
											fontWeight: 600,
											fontSize: '18px',
											color: '#1a3b5d',
											marginBottom: '16px',
											padding: '0 0 12px 0',
											borderBottom: '1px solid #e0e6ed',
										}}
									>
										Committee Secretary
									</Typography>
								</Col>

								<Form.List name="committeeSecretary">
									{(fields, fieldsOpt) => (
										// <DndContext
										// 	sensors={sensors}
										// 	collisionDetection={closestCenter}
										// 	onDragEnd={(e) => handleDragEnd(e, fields, fieldsOpt)}
										// 	modifiers={[restrictToVerticalAxis, restrictToParentElement]}
										// >
										<TableContainer
											sx={{
												'& td, & th': { border: '1px solid rgb(224, 224, 224)', p: 1 },
												'& .ant-form-item': {
													marginBottom: 0,
												},
												mb: 2,
												px: 1,
											}}
										>
											<Table aria-labelledby="committeeSecretaryTable">
												<TableHead sx={{ backgroundColor: 'rgb(246, 245, 245)' }}>
													<TableRow>
														<TableCell sx={{ minWidth: '200px', width: '45%' }}>Name</TableCell>
														<TableCell sx={{ minWidth: '150px', width: '45%' }}>Nominated As</TableCell>
														<TableCell sx={{ minWidth: '100px', width: '10%' }} align="center" width="100">
															Action
														</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{fields?.map((field, index) => (
														<SortableContext key={field.key} items={fields.map((field) => `${field.key}`)}>
															<Form.Item name={[field.name, 'positionType']} noStyle />
															<Form.Item name={[field.name, 'positionValue']} noStyle />

															<TableRow>
																<TableCell>
																	<Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
																		<Form.Item
																			name={[field.name, 'personName']}
																			style={{ margin: 0, flex: 1 }} // remove default margin + expand input
																		>
																			<Input placeholder="Person name" disabled />
																		</Form.Item>
																		<Button
																			type="dashed"
																			shape="circle"
																			icon={<UsergroupAddOutlined />}
																			onClick={() => {
																				setShowModal(true);
																				setSelectedModalRow(field.name);
																				setSelectedModalRow({
																					name: 'committeeSecretary',
																					index: field.name,
																				});
																			}}
																		/>
																	</Box>
																</TableCell>

																<TableCell>
																	<Form.Item
																		name={[field.name, 'nominatedAs']}
																		rules={[
																			{
																				required: true,
																				message: 'Committee role is required.',
																			},
																		]}
																	>
																		<Select
																			allowClear
																			showSearch
																			// style={{ width: '100%' }}
																			placeholder="Select from the list"
																			options={[
																				{
																					label: 'Position',
																					value: 'positions',
																				},
																				{
																					label: 'Employee',
																					value: 'users',
																				},
																				{
																					label: 'Process Role',
																					value: 'processRoles',
																				},
																			]}
																			disabled
																		/>
																	</Form.Item>
																</TableCell>

																<TableCell align="center">
																	{fields.length === 1 && index === 0 && (
																		<Tooltip title="Add Row">
																			{/* <PlusCircleOutlined
																					style={{
																						fontSize: '20px',
																						color: '#1890ff',
																						cursor: 'pointer',
																					}}
																					onClick={() => fieldsOpt.add()}
																				/> */}
																			<AddIconButton onClick={() => fieldsOpt.add()} />
																		</Tooltip>
																	)}

																	{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																		<Tooltip title="Delete Row">
																			{/* <MinusCircleOutlined
																					style={{
																						fontSize: '20px',
																						color: '#ff4d4f',
																						cursor: 'pointer',
																					}}
																					onClick={() => fieldsOpt.remove(field.name)}
																				/> */}
																			<DeleteIconButton onClick={() => fieldsOpt.remove(field.name)} />
																		</Tooltip>
																	)}

																	{fields.length > 1 && index === fields.length - 1 && (
																		<>
																			<Tooltip title="Add Row">
																				{/* <PlusCircleOutlined
																						style={{
																							fontSize: '20px',
																							color: '#1890ff',
																							marginRight: '12px',
																							cursor: 'pointer',
																						}}
																						onClick={() => fieldsOpt.add()}
																					/> */}
																				<AddIconButton onClick={() => fieldsOpt.add()} />
																			</Tooltip>
																			<Tooltip title="Delete Row">
																				{/* <MinusCircleOutlined
																						style={{
																							fontSize: '20px',
																							color: '#ff4d4f',
																							cursor: 'pointer',
																						}}
																						onClick={() => fieldsOpt.remove(field.name)}
																					/> */}
																				<DeleteIconButton onClick={() => fieldsOpt.remove(field.name)} />
																			</Tooltip>
																		</>
																	)}

																	{fields.length > 1 && index === 0 && <span></span>}
																</TableCell>
															</TableRow>
															{/* </DraggableRow> */}
														</SortableContext>
													))}
												</TableBody>
											</Table>
										</TableContainer>
										// </DndContext>
									)}
								</Form.List>

								{/* <Col xs={24} md={8} lg={6}>
									<CustomInputLabel label="quorum" head={true} />
								</Col> */}

								{/* <Col xs={24} md={8} lg={6}>
									<CustomInputLabel label="Holding Time Unit" head={true} />
								</Col> */}

								<Col xs={24} sm={24} md={12} xl={12}>
									<Form.Item
										label="Holding Time Unit"
										name="holdingTime"
										rules={[
											{
												required: true,
												message: 'Holding Time Unit is required',
											},
											({ getFieldValue }) => ({
												validator(_, value) {
													const quorum = getFieldValue('quorum');
													if (quorum === 0 && value !== 0) {
														return Promise.reject(new Error('Holding Time must be 0 when quorum is 0'));
													}
													return Promise.resolve();
												},
											}),
										]}
									>
										<InputNumber min={0} style={{ width: '100%' }} placeholder="Write here" />
									</Form.Item>
								</Col>

								<Col xs={24} sm={24} md={12} xl={12}>
									<Form.Item
										label="Holding Time Unit Of Measurements"
										name="holdingTimeUnit"
										rules={[
											{
												required: true,
												message: 'Holding Time Unit Of Measurements is required',
											},
										]}
									>
										{/* <InputNumber min={1} style={{ width: '100%' }} placeholder="Write here" /> */}
										<Select
											allowClear
											showSearch
											// style={{ width: '100%' }}
											placeholder="Select from the list"
											options={[
												{
													label: 'Hour (s)',
													value: 'hour',
												},
												{
													label: 'Day (s)',
													value: 'days',
												},
												{
													label: 'Week (s)',
													value: 'week',
												},
												{
													label: 'Month (s)',
													value: 'month',
												},
											]}
										/>
									</Form.Item>
								</Col>

								{/* <Col xs={24} md={8} lg={6}>
									<CustomInputLabel label="Holding Time" head={true} />
								</Col> */}

								{/* <Col xs={24} md={8} lg={6}>
									<CustomInputLabel label="Tiebreaker Approval" head={true} />
								</Col> */}
								<Col xs={24} sm={24} md={12} xl={12}>
									<Form.Item
										label="Tiebreaker Approval"
										name="approval"
										rules={[
											{
												required: true,
												message: 'Approval is required',
											},
										]}
									>
										<Radio.Group>
											<Radio value="cWin">Chairside wins</Radio>
											<Radio value="cLoss">Chairside losses</Radio>
										</Radio.Group>
									</Form.Item>
								</Col>

								<Col xs={24} sm={24} md={24} xl={24}>
									{/* <Flex justify="flex-end" align="flex-end">
										<Button
											// className={`${!activate ? 'animated-button' : ''}`}
											// style={{ backgroundColor: activate ? '' : theme.palette.lynTheme.submit }}
											type="primary"
											htmlType="submit"
											// disabled={activate}
										>
											{committeeDetailsState ? 'Update' : 'Submit'}
										</Button>
									</Flex> */}

									<FormSubmitButton detail={committeeDetailsState} />
								</Col>
							</Row>
						</Form>

						{showModal && (
							<SearchMemberModal
								formName={selectedModalRow.name}
								initialValue={form.getFieldValue([selectedModalRow.name, selectedModalRow.index])}
								dataList={dataList}
								showModal={showModal}
								handleClose={() => {
									setShowModal(false);
									setSelectedModalRow(null);
								}}
							/>
						)}
					</Form.Provider>
				</SectionCard>
			</Box>

			{showConfirmationModal && (
				<ComptrollerConfirmModal
					showModal={showConfirmationModal}
					setShowModal={setShowConfirmationModal}
					// setSubmitForm={setSubmitForm}
					modalData={modalInfo}
					handleSubmit={handleSubmit}
				/>
			)}
		</>
	);
}

export default COECommittee;

export const DraggableRow = ({ id, children, screen = 'CREATE', index }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.8 : 1,
		zIndex: isDragging ? 1 : 0,
		position: 'relative',
	};

	return (
		<>
			<TableRow ref={setNodeRef} style={style}>
				{screen?.toLocaleLowerCase() === 'view' && <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>}
				{screen?.toLocaleLowerCase() !== 'view' && (
					<TableCell sx={{ p: 0, textAlign: 'center' }}>
						<IconButton size="small" {...listeners} {...attributes} sx={{ cursor: 'grab' }}>
							<DragIndicatorIcon fontSize="small" />
						</IconButton>
					</TableCell>
				)}
				{children}
			</TableRow>
		</>
	);
};
